import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Tooltip,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import BuildIcon from '@material-ui/icons/Build';

import { SKILL_CHOICES } from '../../../helpers';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginTop: 16,
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    chip: {
      fontWeight: 500,
    },
    empty: {
      color: theme.palette.text.secondary,
    },
    choiceLabel: {
      fontWeight: 500,
    },
    choiceDesc: {
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
    },
    choiceRow: {
      alignItems: 'flex-start',
      marginBottom: theme.spacing(1.5),
    },
  };
});

const choiceOf = (id) => {
  return (
    SKILL_CHOICES.find((c) => {
      return c.id === id;
    }) || { id, name: id, description: '' }
  );
};

// cleaner_skill (plan 013 B2): 1:N per silae_id, UNIQUE(silae_id, skill).
// Rendered as removable chips + an add dialog (drawer-equivalent, kept inline to
// avoid react-admin list routing inside the cleaner show page).
const SkillsCard = ({ silaeId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [skill, setSkill] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = () => {
    if (!silaeId) {
      setRows([]);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    dataProvider
      .getList('cleaner_skill', {
        sort: { field: 'skill', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
        filter: { silae_id: silaeId },
      })
      .then(({ data }) => {
        if (!cancelled) setRows(data || []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(reload, [silaeId, dataProvider]);

  const existingSkills = new Set(
    rows.map((r) => {
      return r.skill;
    }),
  );
  const available = SKILL_CHOICES.filter((c) => {
    return !existingSkills.has(c.id);
  });

  const openAdd = () => {
    setSkill(available.length > 0 ? available[0].id : '');
    setOpen(true);
  };

  const handleAdd = async () => {
    if (!skill || !silaeId) return;
    setSaving(true);
    try {
      await dataProvider.create('cleaner_skill', {
        data: { silae_id: silaeId, skill },
      });
      notify('Compétence ajoutée', 'info');
      setOpen(false);
      setSkill('');
      reload();
    } catch (err) {
      notify(err.message || "Échec de l'ajout", 'warning');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await dataProvider.delete('cleaner_skill', {
        id: row.id,
        previousData: row,
      });
      notify('Compétence retirée', 'info');
      reload();
    } catch (err) {
      notify(err.message || 'Échec de la suppression', 'warning');
    }
  };

  return (
    <Grid item xs={12} className={classes.root}>
      <Card>
        <CardHeader
          avatar={<BuildIcon color="action" />}
          title="Techniques de nettoyage maîtrisées"
          subheader="Compétences métier validées pour l’affectation"
          action={
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openAdd}
              disabled={!loaded || !silaeId || available.length === 0}
            >
              Ajouter
            </Button>
          }
        />
        <CardContent>
          {!silaeId && (
            <Typography variant="body2" className={classes.empty}>
              Aucun matricule (silae_id) — compétences indisponibles.
            </Typography>
          )}
          {silaeId && loaded && rows.length === 0 && (
            <Typography variant="body2" className={classes.empty}>
              Aucune technique renseignée pour le moment.
            </Typography>
          )}
          <div className={classes.chips}>
            {rows.map((row) => {
              const c = choiceOf(row.skill);
              return (
                <Tooltip key={row.id} title={c.description || ''} arrow>
                  <Chip
                    className={classes.chip}
                    color="primary"
                    variant="outlined"
                    label={c.name}
                    onDelete={() => {
                      return handleDelete(row);
                    }}
                  />
                </Tooltip>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => {
          return setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ajouter une technique</DialogTitle>
        <DialogContent>
          {available.length === 0 ? (
            <Typography variant="body2" className={classes.empty}>
              Toutes les techniques sont déjà renseignées.
            </Typography>
          ) : (
            <RadioGroup
              value={skill}
              onChange={(e) => {
                return setSkill(e.target.value);
              }}
            >
              {available.map((c) => {
                return (
                  <FormControlLabel
                    key={c.id}
                    value={c.id}
                    control={<Radio color="primary" />}
                    className={classes.choiceRow}
                    label={
                      <div>
                        <Typography
                          variant="body2"
                          className={classes.choiceLabel}
                        >
                          {c.name}
                        </Typography>
                        {c.description ? (
                          <Typography
                            variant="caption"
                            className={classes.choiceDesc}
                          >
                            {c.description}
                          </Typography>
                        ) : null}
                      </div>
                    }
                  />
                );
              })}
            </RadioGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              return setOpen(false);
            }}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            onClick={handleAdd}
            color="primary"
            variant="contained"
            disabled={saving || !skill}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

SkillsCard.propTypes = {
  silaeId: PropTypes.string,
};

SkillsCard.defaultProps = {
  silaeId: '',
};

export default SkillsCard;
